import React, { Fragment, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import concat from 'lodash/concat';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import styled from 'styled-components';
import Chip from './chip';
import PopoverMenu, { PopoverMenuItem } from './menu';
import SearchInput from './search_input';
import SpinnerTwo from './spinner_two';
import { MenuItemHeader, ScrollingMenu, StyledMenuItem } from './dropdown_menu';
import useMultiselectChips from 'components/hooks/use_multiselect_chips';

/**
 * @visibleName Multiselect Chips
 */

// TODO Add Unit tests for the component
// as requested in https://app.clubhouse.io/gladly/story/93371/unit-tests-for-the-multiselect-chips-component
export default function MultiselectChips({
  'data-aid': dataAid = 'multiselect-chips',
  autoFocus = false,
  className,
  delay = 0,
  disabled,
  error,
  isLoading,
  labelsHash = null,
  maxHeight,
  maxRows,
  maxWidth = '100%',
  onSearchChanged,
  onValuesChanged,
  options = [],
  removableOptions = [],
  sortOptions = true,
  values = [],
}) {
  const {
    addItem,
    availableOptions,
    closeOptions,
    filterValue,
    isOpen,
    memoizedLabels,
    onDelete,
    onInputBlur,
    onKeyDown,
    onTextChange,
    openOptions,
    selectedIndex,
    selectedValues,
    setTargetRef,
    targetRef,
  } = useMultiselectChips({ labelsHash, onSearchChanged, onValuesChanged, options, values, sortOptions });

  const menuContent = isLoading ? (
    <NoContentWrapper>
      <SpinnerTwo size={16} stroke={2} />
    </NoContentWrapper>
  ) : isEmpty(availableOptions) && filterValue ? (
    <NoContentWrapper>No results found. Rephrase your search and try again.</NoContentWrapper>
  ) : (
    availableOptions.map((option, index) =>
      option.type === 'HEADER' ? (
        <MenuItemHeader key={option.label} option={option} />
      ) : (
        <MenuItem
          data-aid={`menuOption-${option.value}`}
          isFocused={selectedIndex === index}
          key={option.value}
          onClose={closeOptions}
          onSelect={() => addItem(option.value)}
          option={option}
        />
      )
    )
  );

  const allOptions = concat(options, removableOptions);

  return (
    <StyledSection className={className} data-aid={dataAid} maxWidth={maxWidth}>
      <StyledSearchInput
        autoFocus={autoFocus}
        data-aid={`${dataAid}-searchInput`}
        delay={delay}
        disabled={disabled}
        hasError={!!error}
        onBlur={onInputBlur}
        onChange={onTextChange}
        onClick={openOptions}
        onKeyDown={onKeyDown}
        setInputRef={setTargetRef}
        value={filterValue}
      />
      {error ? <ErrorLabel data-aid={`${dataAid}-error`}>{error}</ErrorLabel> : null}
      <PopoverMenu
        data-aid={`${dataAid}-menu`}
        fitOpenerWidth
        isOpen={isOpen}
        onClose={closeOptions}
        position="bottom"
        targetPosition="start"
        targetRef={targetRef}
      >
        <ScrollingMenu fitOpenerWidth maxHeight={maxHeight} maxRows={maxRows}>
          {menuContent}
        </ScrollingMenu>
      </PopoverMenu>
      <Chips data-aid={`${dataAid}-selectedItems`} role="list">
        {map(selectedValues, value => {
          const optionForValue = find(allOptions, { value });
          const labelForOption = get(optionForValue, 'label', memoizedLabels[value] || value);

          return (
            <StyledChip
              data-aid={`${dataAid}-selectedItem`}
              disabled={disabled}
              key={value}
              label={labelForOption}
              onDelete={() => onDelete(value)}
              role="listitem"
              value={value}
            />
          );
        })}
      </Chips>
    </StyledSection>
  );
}

function MenuItem({ className, 'data-aid': dataAid, isFocused, onClose, onSelect, option }) {
  const onClick = useCallback(() => {
    onSelect(option.value);
    onClose();
  }, [onClose, onSelect, option.value]);

  let refContainer = useRef(null);

  useEffect(() => {
    isFocused &&
      refContainer &&
      refContainer.current &&
      refContainer.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [isFocused]);

  const content = option.description ? (
    <Fragment>
      <ItemHeader>{option.label}</ItemHeader>
      <ItemDescription>{option.description}</ItemDescription>
    </Fragment>
  ) : (
    option.label
  );

  return (
    <PopoverMenuItem
      className={className}
      component={OverrideMenuItem}
      data-aid="dropdownMenuItem"
      isFocused={isFocused}
      onClick={onClick}
      role="listitem"
    >
      <LabelContainer data-aid={`${dataAid}-label`} ref={isFocused ? refContainer : null}>
        {content}
      </LabelContainer>
    </PopoverMenuItem>
  );
}

export const StyledSearchInput = styled(SearchInput)`
  width: 100%;
`;

const ErrorLabel = styled.div`
  color: ${p => p.theme.colors.red400};
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.6;
`;

const OverrideMenuItem = styled(StyledMenuItem)`
  padding: 8px 24px;
`;
OverrideMenuItem.displayName = 'OverrideMenuItem';

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
LabelContainer.displayName = 'LabelContainer';

const ItemHeader = styled.div`
  font-weight: 700;
`;
ItemHeader.displayName = 'ItemHeader';

const ItemDescription = styled.span``;
ItemDescription.displayName = 'ItemDescription';

const StyledSection = styled.section`
  position: relative;
  width: ${p => p.maxWidth};
`;
StyledSection.displayName = 'StyledSection';

const FilterInputWrapper = styled.div`
  position: relative;
  margin-bottom: 8px;
`;
FilterInputWrapper.displayName = 'FilterInputWrapper';

const Chips = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-grow: 1;
  flex-wrap: wrap;
  padding-top: 8px;
  width: 100%;
`;
Chips.displayName = 'Chips';

const NoContentWrapper = styled.div`
  padding: 8px 16px;
`;
NoContentWrapper.displayName = 'NoResultsContainer';

const StyledChip = styled(Chip)`
  margin-right: 8px;
  margin-bottom: 8px;
`;
StyledChip.displayName = 'StyledChip';

MultiselectChips.propTypes = {
  autoFocus: PropTypes.bool,
  'data-aid': PropTypes.string,
  className: PropTypes.string,
  delay: PropTypes.number,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  labelsHash: PropTypes.objectOf(PropTypes.string),
  maxHeight: PropTypes.string,
  maxRows: PropTypes.number,
  maxWidth: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.exact({
      label: PropTypes.node,
      description: PropTypes.node,
      type: PropTypes.node,
      value: PropTypes.string,
    })
  ),
  onSearchChanged: PropTypes.func,
  onValuesChanged: PropTypes.func,
  removableOptions: PropTypes.arrayOf(
    PropTypes.exact({
      label: PropTypes.node,
      value: PropTypes.string,
    })
  ),
  values: PropTypes.arrayOf(PropTypes.string),
};
